/**
 * TIMMAR SOM EN MÄNNISKA SKRIVER DEM.
 *
 * Kontraktet lagrar arbetstid som MINUTER, heltal: 7,5 h = 450. Montören skriver
 * inte 450. Han skriver "7,5", "7.5" eller "7:30", och alla tre betyder samma
 * sak på ett tak i regn.
 *
 * ui/Falt.ts:s tillHeltal tar decimalformen (både komma och punkt) och är den
 * enda vägen till kontraktets enhet. KLOCKFORMEN "7:30" faller den på, med flit:
 * den är en allmän talomvandlare och 7:30 är inte ett tal. Den här filen lägger
 * till klockformen OVANPÅ den — den räknar aldrig själv om decimalfallet.
 * FUNKTIONEN HÖR EGENTLIGEN HEMMA I ui/Falt.ts så att alla fyra agenter får den;
 * se LUCKOR.
 *
 * MINUTER OCH INTE HALVTIMMAR: "7:45" är 465 minuter, inte 7,75 avrundat till
 * något. Klockformen är exakt just för att den slipper decimalen.
 */

// UR ui/tal.ts OCH INTE UR ui/index.ts. Det är samma funktioner — index.ts
// speglar bara den här filen — men ui/index.ts drar in komponenterna, och då
// följer JSX med. Den här modulen ska gå att köra rakt av under Node, för det
// är HÄR arbetstiden räknas om (test/klienten.ts).
import { FAKTOR, tillHeltal } from '../../ui/tal';
import { MAX_MINUTER_PER_TIDRAD } from '../../api/konstanter';

/** CHECK (minutes BETWEEN 1 AND 1440) i schema/company.sql. */
export const MIN_MINUTER = 1;
export const MAX_MINUTER = MAX_MINUTER_PER_TIDRAD;

const KLOCKA = /^(\d{1,2}):([0-5]?\d)$/;

/**
 * Text -> minuter. null när texten inte är en tid (ännu). Tom sträng ger null
 * och ALDRIG 0 — "inget ifyllt" får inte sparas som en nolltimme.
 */
export const timmarTillMinuter = (text: string): number | null => {
  const rensad = text.replace(/[\s  ]/g, '');
  const klocka = KLOCKA.exec(rensad);
  if (klocka !== null) {
    const timmar = Number(klocka[1]);
    const minuter = Number(klocka[2]);
    if (!Number.isFinite(timmar) || !Number.isFinite(minuter)) return null;
    return timmar * 60 + minuter;
  }
  return tillHeltal(rensad, FAKTOR.timmar_minuter);
};

export const rimligaMinuter = (minuter: number | null): minuter is number =>
  minuter !== null && minuter >= MIN_MINUTER && minuter <= MAX_MINUTER;

/**
 * MINUTER -> TEXT I TIMFÄLTET, utan att tappa en minut. Motsatsen till
 * timmarTillMinuter, och den enda vägen dit när RÖSTEN fyller i fältet: 480
 * blir "8", 450 blir "7,5", men 465 blir "7:45" — INTE "7,8", som franHeltal
 * hade gett och som tillHeltal sedan hade läst som 468. Klockformen finns just
 * för att den slipper decimalen; hela minuter som inte är halvtimmar går den
 * vägen.
 */
export const timtextAv = (minuter: number): string => {
  if (minuter % 60 === 0) return String(minuter / 60);
  if (minuter % 30 === 0) return `${Math.floor(minuter / 60)},5`;
  const h = Math.floor(minuter / 60);
  const m = minuter % 60;
  return `${h}:${m < 10 ? `0${m}` : m}`;
};

/**
 * FÖRDELAR EN TOTAL PÅ FLERA MOMENT.
 *
 * Kontraktet har EN tidrad per moment (TimeEntryWrite bär ett task_id och ett
 * minutes), så "tre och en halv timme, bytte utkastare och provtryckte" blir
 * TVÅ rader. Användaren skriver totalen en gång; resten är räkning.
 *
 * RESTEN LÄGGS PÅ DE FÖRSTA MOMENTEN, en minut i taget. Summan blir alltså
 * EXAKT totalen — en jämn division med avrundning per rad hade tappat eller
 * skapat minuter, och de minuterna hamnar i ett fakturaunderlag.
 */
export const fordelaMinuter = (total: number, antal: number): readonly number[] => {
  if (antal <= 0) return [];
  const bas = Math.floor(total / antal);
  const rest = total - bas * antal;
  return Array.from({ length: antal }, (_, i) => bas + (i < rest ? 1 : 0));
};
